import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import OrderAmount from '../../keyData/OrderAmount';
import OrderService from '../../order/OrderService';
import { setExistingPaymentLink } from '../../../redux/features/keyDataSlice';
import { formatDate } from '../../../utils/dateUtils';

function getLatestOrderDate(orders) {
  if (!orders?.length) return null;
  const dates = orders.map((order) => new Date(order.orderDate));
  const latestDate = new Date(Math.max(...dates));
  return latestDate.toISOString().split('T')[0];
}

function ShopBadge({ shop }) {
  const normalized = String(shop || '').toLowerCase();
  if (normalized === 'smile') {
    return (
      <span className="inline-flex items-center rounded-md border border-pink-300 bg-pink-50 px-2 py-0.5 text-xs font-bold text-pink-800">
        SMILE
      </span>
    );
  }
  if (normalized === 'tama') {
    return (
      <span className="inline-flex items-center rounded-md border border-sky-300 bg-sky-50 px-2 py-0.5 text-xs font-bold text-sky-800">
        TAMA
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-0.5 text-xs font-bold text-gray-700">
      {shop || '-'}
    </span>
  );
}

/**
 * คิว = ใครต้องสรุปยอด (Phat + payment_summary_completions) — เหมือนเดิม
 * รายการ order = โหลดแยกด้วย customerId เหมือน KeyData pendinggroup / NewOrderList (รอเก็บเงิน + shop ตรงโปรไฟล์)
 */
const PaymentSummaryQueue = () => {
  const dispatch = useDispatch();
  const { authState } = useAuth();
  const { user } = authState;

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [pendingDetail, setPendingDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const selectedRow = useMemo(
    () => groups.find((g) => g._key === selectedKey) || null,
    [groups, selectedKey],
  );

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await OrderService.getPendingPaymentSummaryQueue();
      const data = Array.isArray(res.data) ? res.data : [];
      const withKeys = data.map((g) => ({
        ...g,
        _key: `${g.shop}::${g.smileName}`,
      }));
      setGroups(withKeys);
    } catch (e) {
      console.error(e);
      toast.error('โหลดคิวไม่สำเร็จ');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    if (!selectedRow?.customerId) {
      setPendingDetail(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setPendingDetail(null);
      try {
        const res = await OrderService.getPendingOrdersByCustomerId({
          customerId: selectedRow.customerId,
        });
        if (!cancelled) setPendingDetail(res.data);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast.error('โหลดรายการรอจ่ายไม่สำเร็จ');
          setPendingDetail(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRow?.customerId]);

  useEffect(() => {
    return () => {
      dispatch(setExistingPaymentLink(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!pendingDetail) {
      dispatch(setExistingPaymentLink(null));
      return;
    }

    let cancelled = false;
    const { shop, smileName, isCompanyCustomer, orders } = pendingDetail;

    (async () => {
      dispatch(setExistingPaymentLink(null));
      if (!isCompanyCustomer || !orders?.length) return;

      const urls = orders.map((o) => o.url).filter(Boolean);
      if (!urls.length || !smileName) return;

      try {
        const res = await OrderService.checkExistingPaymentLink({
          urls,
          smileName,
          shop,
          paymentType: 'first',
        });
        const response = res.data?.data;
        if (cancelled) return;
        if (response && response.canReuse) {
          dispatch(
            setExistingPaymentLink({
              canReuse: true,
              paymentUrl: response.paymentUrl,
              isCreditCard: response.isCreditCard,
            }),
          );
        }
      } catch (e) {
        if (!cancelled) dispatch(setExistingPaymentLink(null));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingDetail, dispatch]);

  const handleFinish = async () => {
    if (!pendingDetail?.orders?.length) {
      toast.error('ไม่มีรายการรอจ่าย');
      return;
    }
    if (selectedRow?.customerId == null) {
      toast.error('ไม่พบรหัสลูกค้า (customerId)');
      return;
    }
    setFinishing(true);
    try {
      await OrderService.markPaymentSummaryDone({
        customerId: selectedRow.customerId,
      });
      toast.success('บันทึกว่าสรุปยอดแล้ว — รีเฟรชแล้วจะไม่แสดงในรายการนี้');
      setSelectedKey(null);
      setPendingDetail(null);
      dispatch(setExistingPaymentLink(null));
      await loadQueue();
    } catch (e) {
      console.error(e);
      toast.error('บันทึกไม่สำเร็จ');
    } finally {
      setFinishing(false);
    }
  };

  return (
    <div>
      <div className="border-b flex mb-4 pb-3 items-center gap-4">
        <h2 className="text-2xl font-bold">สรุปเก็บเงินลูกค้า (ทุกร้าน)</h2>
        <button
          type="button"
          onClick={() => loadQueue()}
          disabled={loading}
          className="ml-auto rounded-md bg-slate-600 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-500 disabled:opacity-50"
        >
          {loading ? 'กำลังโหลด…' : 'รีเฟรชรายการ'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="bg-gray-100 rounded-lg p-4 min-h-[320px]">
          <div className="text-lg font-bold mb-3">ลูกค้าที่ยังไม่สรุปยอด</div>
          {loading && groups.length === 0 ? (
            <p className="text-gray-600">กำลังโหลด…</p>
          ) : groups.length === 0 ? (
            <p className="text-gray-600">ไม่มีรายการ — หรือสรุปครบแล้วทั้งหมด</p>
          ) : (
            <ul className="space-y-1 max-h-[70vh] overflow-y-auto">
              {groups.map((g) => {
                const isSelected = g._key === selectedKey;
                return (
                  <li key={g._key}>
                    <button
                      type="button"
                      onClick={() => setSelectedKey(g._key)}
                      className={`w-full text-left rounded-md border px-3 py-3 transition-colors ${
                        isSelected
                          ? 'bg-emerald-200 border-emerald-500 ring-2 ring-emerald-400'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">
                        {g.contact} — {g.smileName}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        ร้าน: <span className="font-semibold">
                          <ShopBadge shop={g.shop} />
                        </span>
                        {' · '}
                        {g.pendingOrderCount ?? 0} รายการ
                      </div>
                      {g.latestOrderDate && (
                        <div className="text-xs text-gray-500 mt-1">
                          ล่าสุด: {formatDate(g.latestOrderDate)}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 min-h-[320px]">
          {selectedRow && detailLoading && (
            <div className="flex items-center justify-center h-64 text-gray-600">
              กำลังโหลดรายการรอจ่าย…
            </div>
          )}
          {selectedRow && !detailLoading && pendingDetail && (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">
                  ร้าน: <strong>
                    <ShopBadge shop={pendingDetail.shop} />
                  </strong>
                </span>
                {pendingDetail.orders?.length > 0 && (
                  <span className="text-xs text-gray-500">
                    ล่าสุด: {formatDate(getLatestOrderDate(pendingDetail.orders))}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={finishing}
                  className="ml-auto rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-500 disabled:opacity-50"
                >
                  {finishing ? 'กำลังบันทึก…' : 'เสร็จแล้ว — สรุปยอดให้ลูกค้าแล้ว'}
                </button>
              </div>
              <OrderAmount
                user={user}
                pendingOrdersOverride={pendingDetail}
                shopNameProp={pendingDetail.shop}
              />
            </>
          )}
          {selectedRow && !detailLoading && !pendingDetail?.orders?.length && (
            <div className="flex items-center justify-center h-64 text-gray-500">
              ไม่มี order รอเก็บเงินสำหรับโปรไฟล์นี้
            </div>
          )}
          {!selectedRow && (
            <div className="flex items-center justify-center h-64 text-gray-500">
              เลือกลูกค้าจากรายการด้านซ้ายเพื่อแสดงระบบเก็บเงิน
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSummaryQueue;
